import React, { useState, useEffect, useRef, useMemo } from 'react';
import { find, filter, map, isEmpty } from 'lodash';
import styled from 'styled-components';
import {
  Button,
  Col,
  LabeledIconInput,
  Row,
  SearchIcon,
  LabeledRadio,
  useClickOutside,
} from '@commonsku/styles';

const BugfixLabeledRadio = styled(({ className, iconProps, ...props }) => {
  return <LabeledRadio labelProps={{ className }} className={className} radioIconProps={iconProps} {...props} />;
})`
  &&& svg circle {
    pointer-events: none;
  }
`;

const ColorsColumn = ({ value, colors, onChange }) => {
  const [search, setSearch] = useState('');

  return colors && colors.length > 0 ? (
    <Col style={{
      overflowX: 'hidden', overflowY: 'auto', height: '100%', display: 'flex', flex: '0 0 auto',
      flexDirection: 'column', gap: '4px'
    }}>
      <LabeledIconInput
        Icon={<SearchIcon />}
        type="text"
        placeholder="Find Color"
        iconLabelStyles={{ background: 'white' }}
        iconColor="#B8C4CB"
        style={{
          width: '204px', height: '40px', background: '#FFFFFF', border: '1px solid #B8C4CB', margin: 0, display: 'flex'
        }}
        onChange={(e) => {
          setSearch((e.target.value || '').toLowerCase());
        }}
      />
      {map(filter(colors, (color) => search === '' || color.color_name.toLowerCase().includes(search)), (color) => (
        <div
          key={color.color_name}
          style={{
            width: '204px', background: color.color_name === value?.color_name ? '#FFF' : '#E1F7FA',
            border: color.color_name === value?.color_name ? '2px solid #00B1C8' : '1px solid #B8C4CB',
            borderRadius: '17px', paddingLeft: '5px', paddingTop: '7px', lineHeight: '17px',
            textAlign: 'left', cursor: 'pointer'
          }}
          onClick={() => onChange(color)}
        >
          <BugfixLabeledRadio
            label={color.color_name}
            name="ps-breakdown-color"
            value={color.color_name}
            readOnly={true}
            checked={color.color_name === value?.color_name}
            onChange={() => onChange(color)}
            iconProps={{ style: color.color_name.length > 20 ? { top: '30%' } : null }}
            labelProps={{ style: { color: '#00A0B6' } }}
            style={{ color: '#00A0B6' }}
          />
        </div>
      ))}
    </Col>
  ) : null;
};

const SizesColumn = ({ value, sizes, onChange }) => {
  return sizes && sizes.length > 0 ? (
    <Col style={{
      display: 'flex', flexDirection: 'column', gap: '0.625rem', paddingLeft: '33px', textAlign: 'left', height: '100%',
      overflowY: 'auto', flex: '0 0 260px'
    }}>
      <Row style={{ height: 40, alignItems: 'center' }}>
        <label style={{ flex: '1 1 0' }}>Size</label>
      </Row>
      {map(sizes, size => (
        <Row key={size.size_name} style={{ alignItems: 'center', cursor: 'pointer' }}>
          <Col style={{ flex: '1 1 0' }}>
            <BugfixLabeledRadio
              labelStyle={{ margin: 0 }}
              label={size.size_name}
              name="ps-breakdown-size"
              value={size.size_name}
              readOnly={true}
              checked={value?.size_name === size.size_name}
              onChange={() => onChange(size)}
            />
          </Col>
        </Row>
      ))}
    </Col>
  ) : null;
};

const getColors = (parts) => {
  if (isEmpty(parts)) return [];

  const colorMap = new Map();
  parts.forEach((part) => {
    if (part.colors && Array.isArray(part.colors)) {
      part.colors.forEach(({ color_name }) => {
        if (color_name && !colorMap.has(color_name)) {
          colorMap.set(color_name, {
            color_name: color_name,
            part_ids: []
          });
        }
        if (colorMap.has(color_name)) {
          colorMap.get(color_name).part_ids.push(part.id);
        }
      });
    }
  });

  return Array.from(colorMap.values()).sort((a, b) =>
    a.color_name.localeCompare(b.color_name)
  );
};

const getSizes = (parts, colorFilter = null) => {
  if (isEmpty(parts)) return [];

  const sizeMap = new Map();
  parts.forEach((part) => {
    // Filter by color if provided
    if (colorFilter) {
      const hasColor = part.colors && part.colors.some(c => c.color_name === colorFilter);
      if (!hasColor) return;
    }

    if (part.label_size) {
      if (!sizeMap.has(part.label_size)) {
        sizeMap.set(part.label_size, {
          size_name: part.label_size,
          part_ids: []
        });
      }
      sizeMap.get(part.label_size).part_ids.push(part.id);
    }
  });

  return Array.from(sizeMap.values()).sort((a, b) =>
    a.size_name.localeCompare(b.size_name)
  );
};

const normalizeSize = (size) => (size?.size_name ? size : null);
const normalizeColor = (color) => (color?.color_name ? color : null);

const getSku = (parts, color, size) => {
  if (isEmpty(parts)) return null;

  const normalizedSize = normalizeSize(size);
  const normalizedColor = normalizeColor(color);

  const matchingPart = parts.find((part) => {
    const hasColor =
      !normalizedColor || (part.colors && part.colors.some(c => c.color_name === normalizedColor.color_name));
    const hasSize =
      !normalizedSize || part.label_size === normalizedSize.size_name;

    return hasColor && hasSize;
  });

  if (!matchingPart) return null;

  return {
    id: matchingPart.id,
    ps_part_id: matchingPart.sku,
    color_name: color?.color_name,
    size_name: normalizedSize?.size_name,
  };
};

const PromostandardsBreakdownDetailsDropdown = ({
  parts,
  initialColor,
  initialSize,
  selectedSku,
  onSelect,
  onClose,
  isOpen,
  style
}) => {
  const ref = useRef(null);
  const isOpenRef = useRef(isOpen);
  isOpenRef.current = isOpen;
  useClickOutside({
    ref,
    onClick: () => {
      if (isOpenRef.current && onClose) onClose();
    }
  });

  // Derive selected color and size from selectedSku prop
  const selectedColor = selectedSku?.color_name
    ? { color_name: selectedSku.color_name }
    : null;

  const selectedSize =
    selectedSku?.size_name
      ? { size_name: selectedSku.size_name }
      : null;

  const [pendingColor, setPendingColor] = useState(null);
  const activeColor = pendingColor || selectedColor;

  // Initialize selection on mount
  useEffect(() => {
    if (!parts || selectedSku) return;
    if (!initialColor && !initialSize) return;

    const colors = getColors(parts);
    const sizes = getSizes(parts);
    const hasSizes = sizes.length > 0;

    const matchedColor = initialColor
      ? find(colors, c => c.color_name.toLowerCase() === initialColor.toLowerCase())
      : null;

    const matchedSize = initialSize
      ? find(sizes, s => s.size_name.toLowerCase() === initialSize.toLowerCase())
      : null;

    // if initial value existed but didn't match, DO NOT auto-pick
    if (initialColor && !matchedColor) return;
    if (hasSizes && initialSize && !matchedSize) return;

    const sku = getSku(parts, matchedColor, matchedSize);
    if (sku?.id && onSelect) {
      onSelect({ ...sku, auto_selected: true });
    }
  }, [parts, initialColor, initialSize, selectedSku, onSelect]);

  const handleColorChange = (color) => {
    const hasSizeVariants = getSizes(parts, color?.color_name).length > 0;
    const normalizedSelectedSize = normalizeSize(selectedSize);
    const skuForSelectedSize = (hasSizeVariants && normalizedSelectedSize)
      ? getSku(parts, color, normalizedSelectedSize)
      : null;
    if (hasSizeVariants && !skuForSelectedSize) {
      setPendingColor(color);
      return;
    }
    setPendingColor(null);
    const sku = skuForSelectedSize || getSku(parts, color, null);
    if (sku && onSelect) onSelect({ ...sku, auto_selected: false });
  };

  const handleSizeChange = (size) => {
    const color = activeColor;
    setPendingColor(null);
    const sku = getSku(parts, normalizeColor(color), size);
    if (sku && onSelect) onSelect({ ...sku, auto_selected: false });
  };

  const handleDone = (e) => {
    e.stopPropagation();
    if (onClose) {
      onClose();
    }
  };

  const colors = useMemo(() => getColors(parts), [parts]);
  const sizes = useMemo(() => getSizes(parts, activeColor?.color_name), [parts, activeColor?.color_name]);

  return (
    <Row
      ref={ref}
      onMouseDown={e => e.stopPropagation()}
      style={{
        border: '2px solid #00B1C8', borderRadius: '5px', height: '400px',
        flexWrap: 'nowrap', width: 'max-content', maxWidth: 'calc(100vw - 2rem)',
        padding: '1rem', alignItems: 'stretch', overflowY: 'hidden', position: 'absolute',
        top: 'calc(100% - 15px)',
        right: 0,
        display: isOpen ? 'flex' : 'none',
        visibility: isOpen ? 'visible' : 'hidden',
        ...style
      }}
      className="breakdown-details"
    >
      <ColorsColumn
        value={activeColor}
        colors={colors}
        onChange={handleColorChange}
      />
      {activeColor && <SizesColumn
        value={selectedSize}
        sizes={sizes}
        onChange={handleSizeChange}
      />}
      <Col style={{ position: 'absolute', top: '1rem', right: '1rem' }}>
        <Button
          primary
          size="small"
          onClick={handleDone}
          disabled={!selectedSku?.id}
        >
          Done
        </Button>
      </Col>
    </Row>
  );
};

export default PromostandardsBreakdownDetailsDropdown;